import { useCreatePredictionMarketEntry } from "@/hooks/predictionMarket/useCreatePredictionMarketEntry";
import { useGetPotentialFreePoints } from "@/hooks/predictionMarket/useGetPotentialFreePoints";
import { useHandleError } from "@/hooks/useHandleError";
import { useAuthStore } from "@/store/auth.store";
import type { RomanApiError } from "@roman/shared/lib/net/romanApi";
import type { PredictionMarketChoice } from "@roman/shared/types/PredictionMarket";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface UsePredictionMarketEntryProps {
  predictionMarketId: string;
  selectedChoice: PredictionMarketChoice | null;
  useCompletionModal?: boolean;
}

interface UsePredictionMarketEntryReturn {
  points: number;
  setPoints: React.Dispatch<React.SetStateAction<number>>;
  isEntryConfirmationModalOpen: boolean;
  setIsEntryConfirmationModalOpen: (open: boolean) => void;
  isLoadingPotentialFreePoints: boolean;
  potentialFreePointsError?: RomanApiError;
  potentialFreePoints: string | undefined;
  expectedMultiplier: number;
  expectedReturn: number;
  handleSubmit: () => Promise<void>;
  isMutating: boolean;
}

export const usePredictionMarketEntry = ({
  predictionMarketId,
  selectedChoice,
  useCompletionModal = false,
}: UsePredictionMarketEntryProps): UsePredictionMarketEntryReturn => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const USER_POINTS_DEFAULT = 1000; // The default points for a entry
  const [isEntryConfirmationModalOpen, setIsEntryConfirmationModalOpen] = useState(false);
  const [points, setPoints] = useState(USER_POINTS_DEFAULT);

  const args = selectedChoice
    ? {
        predictionMarketChoiceId: selectedChoice.id,
        freePoints: points.toString(),
      }
    : null;

  const {
    data,
    isLoading: isLoadingPotentialFreePoints,
    error: potentialFreePointsError,
  } = useGetPotentialFreePoints(args, {
    onError: useHandleError("Failed to get potential free points"),
  });

  const { createEntry, isMutating } = useCreatePredictionMarketEntry({
    onError: useHandleError("Failed to create prediction market entry"),
    onSuccess: () => {
      if (useCompletionModal) {
        setIsEntryConfirmationModalOpen(true);
      }
    },
  });

  const potentialFreePoints = data?.potentialFreePoints;
  const expectedMultiplier = potentialFreePoints ? Number((Number(potentialFreePoints) / points).toFixed(2)) : 0;
  const expectedReturn = potentialFreePoints ? Number(potentialFreePoints) : 0;

  const handleSubmit = async () => {
    if (!selectedChoice) return;
    if (!user) {
      navigate("/login");
      return;
    }
    await createEntry({
      predictionMarketId,
      predictionMarketChoiceId: selectedChoice.id,
      freePoints: points.toString(),
    });
  };

  return {
    points,
    setPoints,
    isEntryConfirmationModalOpen,
    setIsEntryConfirmationModalOpen,
    isLoadingPotentialFreePoints,
    potentialFreePointsError,
    potentialFreePoints,
    expectedMultiplier,
    expectedReturn,
    handleSubmit,
    isMutating,
  };
};
